import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Styles/Timeline.css";

const MILESTONES = [
    { phase: "SEQ.00", date: "JUL 24", title: "Concept & charter", detail: "Initial interest meetings and rough weight-class scouting." },
    { phase: "SEQ.01", date: "SEP 24", title: "Team formation", detail: "Roles defined; leadership and subsystem leads onboarded." },
    { phase: "SEQ.02", date: "OCT 24", title: "Sponsorship outreach", detail: "Pitch deck v1, local lab tours, and first sponsor conversations." },
    { phase: "SEQ.03", date: "NOV 24", title: "CAD architecture", detail: "TONE layout frozen; packaging studies for drivetrain and weapon." },
    { phase: "SEQ.04", date: "DEC 24", title: "Design review", detail: "Peer review with faculty; FEA and tolerance pass on critical joints." },
    { phase: "SEQ.05", date: "JAN 25", title: "Electrical bring-up", detail: "ESCs, receivers, and power distribution bench-tested." },
    { phase: "SEQ.06", date: "FEB 25", title: "Software stack", detail: "Drive mixing, failsafes, and logging channels implemented." },
    { phase: "SEQ.07", date: "MAR 25", title: "Chassis fab", detail: "First metal cut; weld / tap / fit-up for main rails." },
    { phase: "SEQ.08", date: "APR 25", title: "Weapon subsystem", detail: "Spinner assembly balanced; shaft and bearing stack validated." },
    { phase: "SEQ.09", date: "JUN 25", title: "Integration", detail: "Full robot dry-build; cable routing and service access tuned." },
    { phase: "SEQ.10", date: "JUL 25", title: "Test & iteration", detail: "Drive practice, weapon spin-ups, and post-test design tweaks." },
    { phase: "SEQ.11", date: "AUG 25", title: "NHRL readiness", detail: "Spares kit, pit checklist, and competition logistics locked." },
    { phase: "EXEC", date: "25–26", title: "Compete", detail: "NHRL events — TONE hits the cage.", highlight: true },
];

function clamp01(n) {
    return Math.min(1, Math.max(0, n));
}

function Timeline() {
    const scrollRegionRef = useRef(null);
    const railShellRef = useRef(null);
    const [progress, setProgress] = useState(0);
    const [activePhase, setActivePhase] = useState(MILESTONES[0].phase);
    const draggingRef = useRef(false);

    const readProgressFromScroll = useCallback(() => {
        const el = scrollRegionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const winH = window.innerHeight;
        const range = el.offsetHeight - winH;
        if (range <= 0) {
            setProgress(1);
            return;
        }
        const scrolled = -rect.top;
        const p = clamp01(scrolled / range);
        setProgress(p);

        const idx = Math.min(MILESTONES.length - 1, Math.floor(p * MILESTONES.length));
        setActivePhase(MILESTONES[idx].phase);
    }, []);

    /** Map scrub position (0–1) to window scroll so timeline region tracks correctly */
    const scrollWindowToProgress = useCallback((p) => {
        const el = scrollRegionRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const docTop = scrollY + rect.top;
        const range = Math.max(1, el.offsetHeight - window.innerHeight);
        const target = docTop + clamp01(p) * range;
        window.scrollTo({ top: Math.max(0, target), behavior: "auto" });
        requestAnimationFrame(readProgressFromScroll);
    }, [readProgressFromScroll]);

    useEffect(() => {
        const update = () => {
            readProgressFromScroll();
        };
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        update();
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [readProgressFromScroll]);

    const progressFromRailClientY = useCallback((clientY) => {
        const rail = railShellRef.current;
        if (!rail) return 0;
        const rr = rail.getBoundingClientRect();
        const y = clientY - rr.top;
        return clamp01(y / rr.height);
    }, []);

    const onRailPointerDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        draggingRef.current = true;
        document.body.classList.add("timeline-scrubbing");

        const move = (ev) => {
            ev.preventDefault();
            const p = progressFromRailClientY(ev.clientY);
            scrollWindowToProgress(p);
        };
        const up = () => {
            draggingRef.current = false;
            document.body.classList.remove("timeline-scrubbing");
            document.removeEventListener("pointermove", move);
            document.removeEventListener("pointerup", up);
            document.removeEventListener("pointercancel", up);
        };

        move(e);
        document.addEventListener("pointermove", move, { passive: false });
        document.addEventListener("pointerup", up);
        document.addEventListener("pointercancel", up);
    };

    const pctDisplay = (progress * 100).toFixed(1).padStart(5, " ");
    const hexApprox = Math.floor(progress * 0xfff)
        .toString(16)
        .toUpperCase()
        .padStart(3, "0");

    return (
        <div className="timeline-page">
            <div className="timeline-hud-bg" aria-hidden="true" />
            <div className="timeline-frame">
                <span className="timeline-corner timeline-corner--tl" aria-hidden="true" />
                <span className="timeline-corner timeline-corner--tr" aria-hidden="true" />
                <span className="timeline-corner timeline-corner--bl" aria-hidden="true" />
                <span className="timeline-corner timeline-corner--br" aria-hidden="true" />

                <header className="timeline-header">
                    <div className="timeline-header-chips">
                        <span className="timeline-chip timeline-chip--live">LIVE</span>
                        <span className="timeline-chip">SYS.BUILD_LOG</span>
                        <span className="timeline-chip timeline-chip--dim">TONE v1.0</span>
                    </div>
                    <h1 className="timeline-h1">
                        <span className="timeline-h1-prefix" aria-hidden="true">
                            {"//"}
                        </span>{" "}
                        Season sequence
                    </h1>
                </header>

                <div className="timeline-scroll-region" ref={scrollRegionRef}>
                    <div className="timeline-layout">
                        <aside className="timeline-rail-aside">
                            <div className="timeline-rail-sticky">
                                <div className="timeline-readout dcrt-panel" aria-live="polite">
                                    <div className="timeline-readout-row">
                                        <span className="timeline-readout-label">PROGRESS</span>
                                        <span className="timeline-readout-value">{pctDisplay}%</span>
                                    </div>
                                    <div className="timeline-readout-row timeline-readout-row--sub">
                                        <span className="timeline-readout-label">ADDR</span>
                                        <span className="timeline-readout-hex">0x{hexApprox}</span>
                                    </div>
                                    <div className="timeline-readout-row timeline-readout-row--sub">
                                        <span className="timeline-readout-label">PHASE</span>
                                        <span className="timeline-readout-phase">{activePhase}</span>
                                    </div>
                                </div>

                                <div className="timeline-channel-wrap">
                                    <span className="timeline-channel-label">CHANNEL</span>
                                    <button
                                        type="button"
                                        className="timeline-rail-shell"
                                        ref={railShellRef}
                                        onPointerDown={onRailPointerDown}
                                        aria-label="Scrub timeline position on main channel"
                                    >
                                        <div className="timeline-rail-ticks" aria-hidden="true">
                                            {Array.from({ length: 9 }).map((_, i) => (
                                                <span key={i} className="timeline-rail-tick" />
                                            ))}
                                        </div>
                                        <div className="timeline-rail-bg" />
                                        <div className="timeline-rail-progress" style={{ height: `${progress * 100}%` }} />
                                        <div
                                            className="timeline-cursor"
                                            style={{ top: `${progress * 100}%`, transform: "translate(-50%, -50%)" }}
                                        />
                                    </button>
                                </div>
                            </div>
                        </aside>

                        <div className="timeline-main">
                            <div className="timeline-main-bar" aria-hidden="true">
                                <span>DATA_STREAM</span>
                                <span className="timeline-main-bar-dots">········</span>
                            </div>
                            <ol className="timeline-milestone-list">
                                {MILESTONES.map((m, i) => {
                                    const threshold = (i + 1) / MILESTONES.length;
                                    const passed = progress >= threshold - 0.02;
                                    return (
                                        <li
                                            key={m.phase + m.date}
                                            className={`timeline-milestone ${passed ? "is-passed" : ""} ${m.highlight ? "is-highlight" : ""}`}
                                        >
                                            <div className="timeline-milestone-rail">
                                                <span className="timeline-milestone-dot" />
                                                <span className="timeline-milestone-beam" aria-hidden="true" />
                                            </div>
                                            <div className="timeline-milestone-card">
                                                <div className="timeline-milestone-meta">
                                                    <span className="timeline-phase">{m.phase}</span>
                                                    <span className="timeline-date">{m.date}</span>
                                                    {passed && <span className="timeline-ack">ACK</span>}
                                                </div>
                                                <h2 className="timeline-milestone-title">{m.title}</h2>
                                                <p className="timeline-milestone-detail">{m.detail}</p>
                                                <div className="timeline-card-footer" aria-hidden="true">
                                                    <span className="timeline-card-hash">CHK:{(i * 97).toString(16).toUpperCase()}</span>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Timeline;
